import { reactive } from "vue";
import { storeToRefs } from "pinia";

import { useAuthStore } from "@/stores/auth";
import { useUserContextStore } from "@/stores/usercontext";

/**
 * Single frontend entry for session identity, mode, resolved company scope, and permission flags.
 *
 * Team doc: ../../docs/session-context.md
 *
 * Invariants (data models unchanged):
 * - Company list + selection live in the company store (see stores/company.js).
 * - Active employee profile is auth.selectedEmployee (stores/auth.js).
 * - Switching profile via EmployeeComboBox updates company selection to match that profile's company.
 *
 * Permission rules stay in auth store getters — this composable only delegates to them.
 */
export function useSession() {
  const authStore = useAuthStore();
  const userContextStore = useUserContextStore();

  const {
    account,
    employees,
    selectedEmployee,
    profileOnly: profileOnlyFlag,
    hasProfile,
    expired,
    isLoggedIn,
    selectedCompany,
    hasFinanceAccess,
    hasTaxationAccess,
    hasHrAccess,
    hasHrSalaryAccess,
    hasCompanyCodeAccess,
    hasCompanyContactsAccess,
  } = storeToRefs(authStore);

  const { which, company, companyId } = storeToRefs(userContextStore);

  /** Alias for which — "employee" | "company" | "unknown" */
  const mode = which;

  /**
   * `reactive()` auto-unwraps the nested refs from storeToRefs, so
   * `can.finance` returns a real boolean in both templates and script.
   */
  const can = reactive({
    finance: hasFinanceAccess,
    taxation: hasTaxationAccess,
    hr: hasHrAccess,
    profileOnly: profileOnlyFlag,
    hrSalary: hasHrSalaryAccess,
    companyCodes: hasCompanyCodeAccess,
    companyContacts: hasCompanyContactsAccess,
  });

  /** Same ref as selectedEmployee — active Hub employee profile */
  const activeProfile = selectedEmployee;

  return {
    account,
    employees,
    selectedEmployee,
    activeProfile,
    hasProfile,
    expired,
    isLoggedIn,
    selectedCompany,
    which,
    mode,
    company,
    companyId,
    can,
  };
}
